import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { selectUserDetails } from '../../../../reducers/userReducer';
import { getContentProviderNameFromId } from '../../../../shared/ContentProvider';
import { Translate } from '../../../../shared/LanguageService';
import ICoordinate from '../../../map/ICoordinate';
import MapService from '../../../map/MapService';
import { CustomerTypes } from '../../Entities';
import { CreateOrderDebugMenu } from '../../../../debug/orders/createOrder/v2/CreateOrderDebugMenu';
import { isTestEnvironment } from '../../../../debug/orders/createOrder/state/debugUrlHelper';
import {
    BookableDays,
    getNextWorkday,
    getStartBookingDate
} from '../../../photographerBooking/se360/v2/shared/bookingDateSelect/state/dateHelper';
import {
    noBookableDays,
    useBookableDaysFrom
} from '../../../photographerBooking/se360/v2/shared/bookingDateSelect/state/useBookableDaysFrom';
import { useAvailablePhotographersQuery } from './queries/useAvailablePhotographersQuery';
import { useLocationQuery } from './queries/useLocationQuery';
import { canBeBookedInCalendar } from './state/bookingRules';
import { useNewOrderQuery } from './queries/useNewOrderQuery';
import { useFirmProductsQuery } from './queries/useFirmProductsQuery';
import { brokerFirmsOf, initialBrokerAgentId, initialBrokerFirmId } from './state/brokerSelection';
import {
    initialPropertyInformation,
    isPropertyInformationComplete
} from './state/propertyInformation';
import {
    additionsFor,
    arePricesShown,
    defaultAdditionIds,
    defaultFloorPlanIds,
    floorPlanIdsAfterChoosingAddition,
    floorPlanProductsOf,
    mainProductsOf,
    orderTotal
} from './state/productRules';
import { AddressFields, addressFromGoogle, GoogleAddressResult } from './state/addressFromGoogle';
import { isLocationSettled, useAddressLocation } from './state/useAddressLocation';
import {
    BookingChoice,
    FloorPlanFile,
    NewOrder,
    ProductTypeTypes,
    PropertyAddress,
    PropertyInformation
} from './types';
import { defaultStoreyId } from './state/storeyNames';
import { CreateOrderLayout, Presentation } from './view/CreateOrderLayout';
import { AddressMap } from './view/AddressMap';
import { AddressOverwriteDialog } from './view/AddressOverwriteDialog';
import { PlaceDetails } from './view/AddressSearch';
import { SelectBrokerStep } from './view/SelectBrokerStep';
import { PropertyInformationStep } from './view/PropertyInformationStep';
import { SelectProductsStep } from './view/SelectProductsStep';
import { SelectAdditionsStep } from './view/SelectAdditionsStep';
import { FloorPlansStep } from './view/FloorPlansStep';
import { FloorPlanUpload } from './view/FloorPlanUpload';
import { PhotographerTimeline } from './view/PhotographerTimeline';
import { PhotographerChoiceList } from './view/PhotographerChoiceList';
import { BookingSummary } from './view/BookingSummary';

const NUMBER_OF_STEPS = 5;

const addressTextFields: Array<keyof AddressFields> = [
    'addressLine1',
    'addressPostalNumber',
    'addressCity',
    'addressDistrict'
];

enum Step {
    Broker = 1,
    PropertyInformation = 2,
    Products = 3,
    Additions = 4,
    FloorPlans = 5,
    Photographer = 6
}

function Loading() {
    return (
        <Box sx={{ display: 'grid', gap: '16px', padding: '16px' }} aria-busy="true">
            <Skeleton variant="text" width="60%" height={32} />
            {[0, 1, 2].map((block) => (
                <Skeleton key={block} variant="rectangular" height={64} />
            ))}
        </Box>
    );
}

function uniqueInOrder(values: string[]): string[] {
    return values.filter((value, index) => values.indexOf(value) === index);
}

type CreateOrderStepsProps = {
    newOrder: NewOrder;
    loggedInUserId: number;
    notice: React.ReactNode;
};

function CreateOrderSteps({ newOrder, loggedInUserId, notice }: CreateOrderStepsProps) {
    let [step, setStep] = React.useState(Step.Broker);
    let [brokerAgentId, setBrokerAgentId] = React.useState(() =>
        initialBrokerAgentId(newOrder, loggedInUserId)
    );
    let [brokerFirmId, setBrokerFirmId] = React.useState(() =>
        initialBrokerFirmId(newOrder, brokerAgentId)
    );
    let [propertyInformation, setPropertyInformation] = React.useState<PropertyInformation>(() =>
        initialPropertyInformation(newOrder.pendingOrder)
    );
    let [chosenProductId, setChosenProductId] = React.useState<number | null>(null);
    let [chosenAdditionIds, setChosenAdditionIds] = React.useState<number[]>([]);
    let [wantedProductTypeName, setWantedProductTypeName] = React.useState<string | null>(null);
    let [isAddressFromMap, setIsAddressFromMap] = React.useState(false);
    let latestMapPick = React.useRef(0);
    let [pinWaitingForApproval, setPinWaitingForApproval] = React.useState<ICoordinate | null>(
        null
    );
    let [wantedFloorPlanIds, setWantedFloorPlanIds] = React.useState<number[] | null>(null);
    let [floorPlanFiles, setFloorPlanFiles] = React.useState<FloorPlanFile[]>([]);
    let [defaultBookingDate] = React.useState(() => getNextWorkday(new Date(Date.now())));
    let [wantedBookingDate, setWantedBookingDate] = React.useState<Date | null>(null);
    let [bookableDays, setBookableDays] = React.useState<BookableDays>(noBookableDays);
    let [wantedBookingChoice, setWantedBookingChoice] = React.useState<BookingChoice | null>(null);
    let theme = useTheme();
    let isSmallerScreen = useMediaQuery(theme.breakpoints.down('sm'));

    let addressLocation = useAddressLocation(propertyInformation.address);
    let mapLocation =
        addressLocation.status === 'found' || addressLocation.status === 'picked'
            ? addressLocation.location
            : null;
    let productsQuery = useFirmProductsQuery(brokerFirmId);
    let existingOrderData = newOrder.existingOrderData || null;
    let existingPlanOrderId =
        existingOrderData &&
        existingOrderData.existingPlanOrderId &&
        !existingOrderData.allowPlanOrder
            ? existingOrderData.existingPlanOrderId
            : null;
    let products = (productsQuery.data || []).filter(
        (product) =>
            existingPlanOrderId === null || product.productTypeType !== ProductTypeTypes.Plan
    );
    let floorPlans = floorPlanProductsOf(products);
    let chosenFloorPlanIds =
        wantedFloorPlanIds !== null
            ? wantedFloorPlanIds
            : existingOrderData && existingOrderData.existingPlanOrderId
            ? []
            : defaultFloorPlanIds(products);
    let mainProducts = mainProductsOf(products);
    let productTypeNames = uniqueInOrder(mainProducts.map((product) => product.productTypeName));
    let shownProductTypeName =
        wantedProductTypeName !== null && productTypeNames.includes(wantedProductTypeName)
            ? wantedProductTypeName
            : productTypeNames[0] || null;

    let brokerFirms = brokerFirmsOf(newOrder, brokerAgentId);
    let brokerFirm = brokerFirms.find((firm) => firm.id === brokerFirmId);
    let isCommercial = brokerFirm ? brokerFirm.customerType === CustomerTypes.Commercial : false;
    let chosenAdditions = additionsFor(products, chosenProductId).filter((product) =>
        chosenAdditionIds.includes(product.id)
    );
    let chosenProducts = [
        ...products.filter((product) => product.id === chosenProductId),
        ...chosenAdditions,
        ...floorPlans.filter((product) => chosenFloorPlanIds.includes(product.id))
    ];
    let isBookableInCalendar = canBeBookedInCalendar(brokerFirm, chosenProducts);
    let isOnPhotographerStep = step === Step.Photographer;
    let chosenProductIds = chosenProducts.map((product) => product.id);
    let bookingDate = getStartBookingDate(wantedBookingDate, defaultBookingDate, bookableDays);
    let photographersQuery = useAvailablePhotographersQuery({
        address: {
            street: propertyInformation.address.addressLine1,
            zip: propertyInformation.address.addressPostalNumber,
            city: propertyInformation.address.addressCity
        },
        productIds: chosenProductIds,
        brokerFirmId,
        bookingDate,
        enabled: isOnPhotographerStep && isBookableInCalendar
    });
    let locationQuery = useLocationQuery(
        bookingDate,
        mapLocation,
        isOnPhotographerStep && isBookableInCalendar && !isSmallerScreen
    );
    let isTakingOverBookableDays = useBookableDaysFrom(
        photographersQuery.data,
        bookableDays,
        setBookableDays
    );
    let isLoadingPhotographers = photographersQuery.isLoading || isTakingOverBookableDays;
    let availablePhotographers = photographersQuery.data
        ? photographersQuery.data.availablePhotographers
        : [];
    let chosenPhotographer = wantedBookingChoice
        ? availablePhotographers.find(
              (photographer) => photographer.id === wantedBookingChoice.photographerId
          )
        : undefined;
    let bookingChoice =
        chosenPhotographer &&
        chosenPhotographer.availableTimeslots.some(
            (timeslot) => timeslot.time === wantedBookingChoice.timeslot
        )
            ? wantedBookingChoice
            : null;

    function changeBookingDate(newBookingDate: Date) {
        setWantedBookingDate(newBookingDate);
        setWantedBookingChoice(null);
    }

    let connectedContentProviderName = newOrder.pendingOrder
        ? getContentProviderNameFromId(newOrder.pendingOrder.contentProvider)
        : null;

    function changeBrokerFirm(newBrokerFirmId: number | null) {
        setBrokerFirmId(newBrokerFirmId);
        setChosenProductId(null);
        setChosenAdditionIds([]);
        setWantedProductTypeName(null);
        setWantedFloorPlanIds(null);
    }

    function changeBrokerAgent(newBrokerAgentId: number) {
        setBrokerAgentId(newBrokerAgentId);
        changeBrokerFirm(initialBrokerFirmId(newOrder, newBrokerAgentId));
    }

    function changeAddress(fields: Partial<PropertyAddress>) {
        setPropertyInformation((current) => ({
            ...current,
            address: { ...current.address, ...fields }
        }));
    }

    function forgetPendingMapPick() {
        latestMapPick.current += 1;
    }

    function changePropertyInformation(changed: PropertyInformation) {
        let isAddressEdited = addressTextFields.some(
            (field) => changed.address[field] !== propertyInformation.address[field]
        );
        if (isAddressEdited) {
            forgetPendingMapPick();
            setIsAddressFromMap(false);
            setPropertyInformation({
                ...changed,
                address: { ...changed.address, addressPointLocation: null }
            });
            return;
        }
        setPropertyInformation(changed);
    }

    function fillPropertyInformationForDebugging(filled: PropertyInformation) {
        forgetPendingMapPick();
        setIsAddressFromMap(false);
        setPropertyInformation((current) => ({
            ...filled,
            notes: current.notes,
            extraDeliveryRecipients: current.extraDeliveryRecipients
        }));
    }

    function chooseAddress(place: PlaceDetails) {
        forgetPendingMapPick();
        setIsAddressFromMap(false);
        changeAddress({ ...addressFromGoogle(place), addressPointLocation: null });
    }

    function movePinAndFillAddress(location: ICoordinate) {
        changeAddress({ addressPointLocation: location });
        forgetPendingMapPick();
        let thisPick = latestMapPick.current;
        MapService.getAddresFromPosition(location.longitude, location.latitude).then(
            (result: GoogleAddressResult | null) => {
                if (result && thisPick === latestMapPick.current) {
                    changeAddress(addressFromGoogle(result));
                    setIsAddressFromMap(true);
                }
            }
        );
    }

    function pickLocation(location: ICoordinate) {
        let { addressLine1, addressPostalNumber, addressCity } = propertyInformation.address;
        let isAddressWritten = Boolean(addressLine1 || addressPostalNumber || addressCity);

        if (isAddressWritten && !isAddressFromMap) {
            setPinWaitingForApproval(location);
            return;
        }
        movePinAndFillAddress(location);
    }

    function approvePinMove() {
        setIsAddressFromMap(true);
        movePinAndFillAddress(pinWaitingForApproval);
        setPinWaitingForApproval(null);
    }

    function chooseProduct(productId: number) {
        setChosenProductId(productId);
        setChosenAdditionIds(defaultAdditionIds(products, productId));
    }

    function toggleAddition(productId: number) {
        if (chosenAdditionIds.includes(productId)) {
            setChosenAdditionIds(chosenAdditionIds.filter((id) => id !== productId));
            return;
        }
        setChosenAdditionIds([...chosenAdditionIds, productId]);
        setWantedFloorPlanIds(
            floorPlanIdsAfterChoosingAddition(products, productId, chosenFloorPlanIds)
        );
    }

    function toggleFloorPlan(productId: number) {
        setWantedFloorPlanIds(
            chosenFloorPlanIds.includes(productId)
                ? chosenFloorPlanIds.filter((id) => id !== productId)
                : [...chosenFloorPlanIds, productId]
        );
    }

    let total =
        step >= Step.Products && arePricesShown(products)
            ? {
                  amount: orderTotal(
                      products,
                      chosenProductId,
                      chosenAdditionIds,
                      chosenFloorPlanIds
                  ),
                  currencySign: products[0].currencySign
              }
            : null;

    let map = (
        <AddressMap
            location={mapLocation}
            proposedLocation={pinWaitingForApproval}
            onPick={pickLocation}
        />
    );
    let isDebugMenuShown = window.appConfig.debugEnabled && isTestEnvironment(window.location.href);
    let floorPlanUpload = (
        <FloorPlanUpload
            files={floorPlanFiles}
            onAddFiles={(added) =>
                setFloorPlanFiles([
                    ...floorPlanFiles,
                    ...added.map((file) => ({ file, storeyId: defaultStoreyId }))
                ])
            }
            onChangeStorey={(changedIndex, storeyId) =>
                setFloorPlanFiles(
                    floorPlanFiles.map((floorPlanFile, index) =>
                        index === changedIndex ? { ...floorPlanFile, storeyId } : floorPlanFile
                    )
                )
            }
            onRemoveFile={(removedIndex) =>
                setFloorPlanFiles(floorPlanFiles.filter((file, index) => index !== removedIndex))
            }
        />
    );
    let productTypePresentation: Presentation | null = shownProductTypeName
        ? {
              label: shownProductTypeName,
              content: (
                  <Box
                      sx={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                      }}
                  >
                      <Typography component="h2" sx={{ fontSize: '36px', fontWeight: 'bold' }}>
                          {shownProductTypeName}
                      </Typography>
                  </Box>
              )
          }
        : null;

    let steps: Record<
        Step,
        {
            title: string;
            canContinue: boolean;
            nextLabel?: string;
            presentation: Presentation | null;
            content: React.ReactNode;
        }
    > = {
        [Step.Broker]: {
            title: Translate('order/create/v2/step/broker/title'),
            canContinue: brokerAgentId !== null && brokerFirmId !== null,
            presentation: null,
            content: (
                <SelectBrokerStep
                    brokerAgents={newOrder.availableBrokerAgents}
                    brokerFirms={brokerFirms}
                    brokerAgentId={brokerAgentId}
                    brokerFirmId={brokerFirmId}
                    connectedContentProviderName={connectedContentProviderName}
                    onBrokerAgentChange={changeBrokerAgent}
                    onBrokerFirmChange={changeBrokerFirm}
                />
            )
        },
        [Step.PropertyInformation]: {
            title: Translate('order/create/v2/step/property-information/title'),
            canContinue:
                isPropertyInformationComplete(propertyInformation) &&
                isLocationSettled(addressLocation),
            presentation: {
                label: Translate('order/create/v2/map/label'),
                content: <Box sx={{ flex: 1 }}>{map}</Box>
            },
            content: (
                <PropertyInformationStep
                    propertyInformation={propertyInformation}
                    objectTypes={
                        isCommercial
                            ? newOrder.availableCommercialObjectTypes
                            : newOrder.availablePrivateObjectTypes
                    }
                    isCommercial={isCommercial}
                    addressLocation={addressLocation}
                    map={map}
                    debugMenu={
                        isDebugMenuShown && (
                            <CreateOrderDebugMenu onFill={fillPropertyInformationForDebugging} />
                        )
                    }
                    onChange={changePropertyInformation}
                    onChooseAddress={chooseAddress}
                    onRemovePickedLocation={() => changeAddress({ addressPointLocation: null })}
                />
            )
        },
        [Step.Products]: {
            title: Translate('order/create/v2/step/products/title'),
            canContinue: chosenProductId !== null,
            presentation: productTypePresentation,
            content: productsQuery.isLoading ? (
                <Loading />
            ) : (
                <SelectProductsStep
                    products={mainProducts}
                    productTypeNames={productTypeNames}
                    shownProductTypeName={shownProductTypeName}
                    chosenProductId={chosenProductId}
                    onShowProductType={setWantedProductTypeName}
                    onChooseProduct={chooseProduct}
                />
            )
        },
        [Step.Additions]: {
            title: Translate('order/create/v2/step/additions/title'),
            canContinue: true,
            presentation: null,
            content: (
                <SelectAdditionsStep
                    additions={additionsFor(products, chosenProductId)}
                    chosenAdditionIds={chosenAdditionIds}
                    onToggleAddition={toggleAddition}
                />
            )
        },
        [Step.FloorPlans]: {
            title: Translate('order/create/v2/step/floor-plans/title'),
            canContinue: true,
            nextLabel: Translate('order/create/v2/button/choose-photographer'),
            presentation: null,
            content: (
                <FloorPlansStep
                    floorPlans={floorPlans}
                    chosenFloorPlanIds={chosenFloorPlanIds}
                    existingPlanOrderId={existingPlanOrderId}
                    upload={floorPlanUpload}
                    onToggleFloorPlan={toggleFloorPlan}
                />
            )
        },
        [Step.Photographer]: {
            title: Translate('order/create/v2/step/photographer/title'),
            canContinue: false,
            nextLabel: Translate('order/create/v2/button/book'),
            presentation: isBookableInCalendar
                ? {
                      label: Translate('order/create/v2/step/photographer/timeline'),
                      content: (
                          <Box
                              sx={{
                                  flex: 1,
                                  display: 'flex',
                                  padding: '24px',
                                  backgroundColor: 'background.paper',
                                  color: 'text.primary',
                                  overflowX: 'auto'
                              }}
                          >
                              <PhotographerTimeline
                                  photographers={availablePhotographers}
                                  bookingDate={bookingDate}
                                  choice={bookingChoice}
                                  locationData={locationQuery.data}
                                  isLoading={isLoadingPhotographers}
                                  onChangeDate={changeBookingDate}
                                  onChoose={setWantedBookingChoice}
                                  earliestBookableDate={bookableDays.earliestBookableDate}
                              />
                          </Box>
                      )
                  }
                : null,
            content: (
                <Box sx={{ display: 'grid', gap: '16px' }}>
                    {isSmallerScreen && isBookableInCalendar && (
                        <PhotographerChoiceList
                            photographers={availablePhotographers}
                            bookingDate={bookingDate}
                            choice={bookingChoice}
                            isLoading={isLoadingPhotographers}
                            onChangeDate={changeBookingDate}
                            onChoose={setWantedBookingChoice}
                            earliestBookableDate={bookableDays.earliestBookableDate}
                        />
                    )}
                    <BookingSummary
                        isBookableInCalendar={isBookableInCalendar}
                        isSmallerScreen={isSmallerScreen}
                        photographerName={
                            bookingChoice && chosenPhotographer ? chosenPhotographer.name : null
                        }
                        bookingDate={bookingDate}
                        timeslot={bookingChoice ? bookingChoice.timeslot : null}
                        address={propertyInformation.address}
                        chosenProducts={chosenProducts}
                        total={total}
                    />
                </Box>
            )
        }
    };
    let current = steps[step];

    return (
        <>
            <AddressOverwriteDialog
                isOpen={pinWaitingForApproval !== null}
                onOverwrite={approvePinMove}
                onKeepAddress={() => setPinWaitingForApproval(null)}
            />
            <CreateOrderLayout
                notice={notice}
                title={current.title}
                stepNumber={step === Step.Photographer ? null : step}
                numberOfSteps={NUMBER_OF_STEPS}
                presentation={current.presentation}
                total={total}
                canContinue={current.canContinue}
                nextLabel={current.nextLabel || Translate('order/create/v2/button/next')}
                onBack={step > Step.Broker ? () => setStep(step - 1) : null}
                onNext={() => setStep(step + 1)}
            >
                {current.content}
            </CreateOrderLayout>
        </>
    );
}

type CreateOrderPageProps = RouteComponentProps & {
    notice?: React.ReactNode;
};

function CreateOrderPage({ location, notice }: CreateOrderPageProps) {
    let pendingOrderId = new URLSearchParams(location.search).get('pendingOrderId');
    let userDetails = useSelector(selectUserDetails);
    let newOrderQuery = useNewOrderQuery(pendingOrderId);

    if (newOrderQuery.isError) {
        return <Alert severity="error">{Translate('order/create/v2/loading-failed')}</Alert>;
    }

    if (!newOrderQuery.data) {
        return <Loading />;
    }

    return (
        <CreateOrderSteps
            newOrder={newOrderQuery.data}
            loggedInUserId={userDetails.id}
            notice={notice || null}
        />
    );
}

export default withRouter(CreateOrderPage);
